import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsEquipment } from './useData';
import {
  pmsComponentField,
  pmsCriticalityField,
  pmsDepartmentField,
  pmsEquipmentStatusField,
  pmsLookupSelect,
  pmsRecordStatusField,
  pmsSubComponentField,
  pmsSystemField,
  pmsVerificationStatusField,
  pmsVesselField} from '../shared/pmsFormFields';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';

export const moduleConfig = {
  module: {
    title: "Equipment",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-equipment',
      new: '/pms/pms-equipment/new',
      detail: (id: number) => `/pms/pms-equipment/${id}`,
      edit: (id: number) => `/pms/pms-equipment/${id}/edit`},
    api: { list: '/pms-equipment', detail: (id: number) => `/pms-equipment/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsEquipment[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-equipment/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsEquipment) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsEquipment } = await import('../../../services/mutations');
      await deletePmsEquipment(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Equipment",
    getTitle: (item: PmsEquipment) => item.name ?? `#${item.id}`},
  form: {
    new: { title: 'New equipment', submitText: 'Create' },
    edit: { title: 'Edit equipment', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-5xl',
    create: async (data: Partial<PmsEquipment>) => {
      const { createPmsEquipment } = await import('../../../services/mutations');
      const {
        vessel,
        department,
        system,
        component,
        subComponent,
        parentEquipment,
        criticality,
        status,
        recordStatus,
        verificationStatus,
        id,
        ...payload
      } = data as Record<string, unknown>;
      return createPmsEquipment(payload);
    },
    update: async (id: number, data: Partial<PmsEquipment>) => {
      const { updatePmsEquipment } = await import('../../../services/mutations');
      const {
        vessel,
        department,
        system,
        component,
        subComponent,
        parentEquipment,
        criticality,
        status,
        recordStatus,
        verificationStatus,
        id: _id,
        ...payload
      } = data as Record<string, unknown>;
      return updatePmsEquipment(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Vessel this equipment slot belongs to',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('departmentId', '');
        setFieldValue('systemId', '');
        setFieldValue('componentId', '');
        setFieldValue('subComponentId', '');
        setFieldValue('parentEquipmentId', '');
      }},
    {
      ...pmsDepartmentField(true),
      helperText: 'Owning department on this vessel',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('systemId', '');
        setFieldValue('componentId', '');
        setFieldValue('subComponentId', '');
        setFieldValue('parentEquipmentId', '');
      }},
    {
      ...pmsSystemField(false),
      helperText: 'Optional. Place under an equipment system',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('componentId', '');
        setFieldValue('subComponentId', '');
      }},
    {
      ...pmsComponentField(false),
      helperText: 'Optional. Place under a component',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('subComponentId', '');
      }},
    {
      ...pmsSubComponentField(false),
      helperText: 'Optional. Place under a sub-component'},
    {
      ...pmsLookupSelect({
        name: 'parentEquipmentId',
        label: 'Parent equipment',
        apiEndpoint: '/pms-equipment',
        required: false,
        clearable: true,
        placeholder: 'None — top-level equipment',
        optionsDependOnFields: ['vesselId', 'departmentId'],
        optionsEndpointFromFormData: () => '/pms-equipment',
        filterDynamicOptionItem: (item, fd) => {
          if (fd.id && Number(item.id) === Number(fd.id)) return false;
          if (fd.vesselId && Number(item.vesselId) !== Number(fd.vesselId)) return false;
          if (fd.departmentId && Number(item.departmentId) !== Number(fd.departmentId)) return false;
          return true;
        }}),
      helperText: 'Optional. Nest under another equipment slot on this vessel'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Main engine — unit 1',
      validation: { required: true }},
    {
      name: 'equipmentType',
      label: 'Equipment type',
      type: 'text',
      placeholder: 'e.g. Diesel engine, Pump, Generator'},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Functional role of this equipment slot'},
    {
      name: 'manufacturer',
      label: 'Manufacturer',
      type: 'text',
      placeholder: 'e.g. MAN Energy Solutions'},
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      placeholder: 'e.g. 6S50ME-C'},
    {
      name: 'serialNumber',
      label: 'Serial number',
      type: 'text',
      placeholder: 'Maker serial (if known)'},
    {
      name: 'location',
      label: 'Location',
      type: 'text',
      placeholder: 'e.g. Engine room — deck 2'},
    {
      ...pmsCriticalityField(true),
      helperText: 'How critical this equipment is to vessel operations'},
    {
      ...pmsEquipmentStatusField(true),
      helperText: 'Operational status of the equipment slot'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this equipment record'},
    {
      ...pmsVerificationStatusField(true),
      helperText: 'Verification / class check state'},
    {
      name: 'isRotatingEquipment',
      label: 'Rotating',
      type: 'switch',
      switchLabel: 'Rotating equipment (supports instance swaps)'},
    {
      name: 'isSafetyCritical',
      label: 'Safety',
      type: 'switch',
      switchLabel: 'Safety critical'},
    {
      name: 'isClassRelated',
      label: 'Class',
      type: 'switch',
      switchLabel: 'Class related'},
    {
      name: 'isCylinderSpecific',
      label: 'Cylinder',
      type: 'switch',
      switchLabel: 'Cylinder specific'},
    {
      name: 'cylinderNumber',
      label: 'Cylinder number',
      type: 'number',
      min: 1,
      placeholder: 'e.g. 1',
      helperText: 'Required when this equipment is cylinder-specific',
      validation: {
        requiredWhen: (fd) => !!fd.isCylinderSpecific,
        message: 'Enter the cylinder number'}},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Equipment is active'},
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      rows: 3,
      placeholder: 'Internal notes for planners and crew'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Placement',
        description: 'Where this equipment slot sits in the vessel hierarchy.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'vesselId',
          'departmentId',
          'systemId',
          'componentId',
          'subComponentId',
          'parentEquipmentId',
        ],
        fieldSpans: {
          vesselId: 'md:col-span-4',
          departmentId: 'md:col-span-4',
          systemId: 'md:col-span-4',
          componentId: 'md:col-span-4',
          subComponentId: 'md:col-span-4',
          parentEquipmentId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Equipment',
        description: 'Identity and maker details for this functional slot.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'name',
          'equipmentType',
          'description',
          'manufacturer',
          'model',
          'serialNumber',
          'location',
        ],
        fieldSpans: {
          name: 'md:col-span-8',
          equipmentType: 'md:col-span-4',
          description: 'md:col-span-12',
          manufacturer: 'md:col-span-4',
          model: 'md:col-span-4',
          serialNumber: 'md:col-span-4',
          location: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'classification',
        title: 'Classification',
        description: 'Criticality and status lookups used across PMS.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['criticalityId', 'statusId', 'recordStatusId', 'verificationStatusId'],
        fieldSpans: {
          criticalityId: 'md:col-span-3',
          statusId: 'md:col-span-3',
          recordStatusId: 'md:col-span-3',
          verificationStatusId: 'md:col-span-3'},
        collapsible: false},
      {
        id: 'flags',
        title: 'Characteristics',
        description: 'Operational flags that affect scheduling and instance handling.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'isRotatingEquipment',
          'isSafetyCritical',
          'isClassRelated',
          'isCylinderSpecific',
        ],
        fieldSpans: {
          isRotatingEquipment: 'md:col-span-3',
          isSafetyCritical: 'md:col-span-3',
          isClassRelated: 'md:col-span-3',
          isCylinderSpecific: 'md:col-span-3'},
        collapsible: false},
      {
        id: 'cylinder',
        title: 'Cylinder',
        description: 'Shown when this equipment is marked cylinder-specific.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['cylinderNumber'],
        fieldSpans: { cylinderNumber: 'md:col-span-4' },
        collapsible: false,
        visibleWhen: (fd) => !!fd.isCylinderSpecific,
        visibilityDependsOn: ['isCylinderSpecific']},
      {
        id: 'status',
        title: 'Status & notes',
        description: 'Whether the slot is in the active plan, plus planner notes.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive', 'notes'],
        fieldSpans: {
          isActive: 'md:col-span-4',
          notes: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const codeName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsEquipment) => {
  const v = item.vessel;
  if (!v && item.vesselId == null) return '-';
  const name = v?.vesselName ?? v?.name;
  return name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

const labelParentEquipment = (item: PmsEquipment) => {
  const p = item.parentEquipment;
  return p?.name ?? (item.parentEquipmentId != null ? `#${item.parentEquipmentId}` : '-');
};

const yesNo = (v?: boolean | null) => (v ? 'Yes' : 'No');

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsEquipment>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsEquipment, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      { label: 'Department', value: codeName(item.department, item.departmentId) },
      { label: 'System', value: codeName(item.system, item.systemId) },
      { label: 'Component', value: codeName(item.component, item.componentId) },
      { label: 'Sub-component', value: codeName(item.subComponent, item.subComponentId) },
      { label: 'Parent equipment', value: labelParentEquipment(item) },
      { label: 'Name', value: item.name != null ? String(item.name) : '' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Equipment type', value: item.equipmentType != null ? String(item.equipmentType) : '-' },
      { label: 'Manufacturer', value: item.manufacturer != null ? String(item.manufacturer) : '-' },
      { label: 'Model', value: item.model != null ? String(item.model) : '-' },
      { label: 'Serial number', value: item.serialNumber != null ? String(item.serialNumber) : '-' },
      { label: 'Location', value: item.location != null ? String(item.location) : '-' },
      { label: 'Criticality', value: codeName(item.criticality, item.criticalityId) },
      { label: 'Status', value: codeName(item.status, item.statusId) },
      { label: 'Record status', value: codeName(item.recordStatus, item.recordStatusId) },
      { label: 'Verification', value: codeName(item.verificationStatus, item.verificationStatusId) },
      { label: 'Rotating equipment', value: yesNo(item.isRotatingEquipment) },
      { label: 'Safety critical', value: yesNo(item.isSafetyCritical) },
      { label: 'Class related', value: yesNo(item.isClassRelated) },
      { label: 'Cylinder specific', value: yesNo(item.isCylinderSpecific) },
      { label: 'Cylinder number', value: item.cylinderNumber != null ? String(item.cylinderNumber) : '-' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Notes', value: item.notes != null ? String(item.notes) : '-' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsEquipment> => ({
  vesselId: undefined,
  departmentId: undefined,
  systemId: undefined,
  componentId: undefined,
  subComponentId: undefined,
  parentEquipmentId: undefined,
  name: '',
  description: '',
  equipmentType: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  location: '',
  criticalityId: undefined,
  statusId: undefined,
  recordStatusId: undefined,
  verificationStatusId: undefined,
  isRotatingEquipment: false,
  isSafetyCritical: false,
  isClassRelated: false,
  isCylinderSpecific: false,
  cylinderNumber: undefined,
  isActive: true,
  notes: ''});
export const mapItemToFormData = (item: PmsEquipment): Partial<PmsEquipment> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  departmentId: item.departmentId != null ? Number(item.departmentId) : undefined,
  systemId: item.systemId != null ? Number(item.systemId) : undefined,
  componentId: item.componentId != null ? Number(item.componentId) : undefined,
  subComponentId: item.subComponentId != null ? Number(item.subComponentId) : undefined,
  parentEquipmentId: item.parentEquipmentId != null ? Number(item.parentEquipmentId) : undefined,
  name: item.name ?? '',
  description: item.description ?? '',
  equipmentType: item.equipmentType ?? '',
  manufacturer: item.manufacturer ?? '',
  model: item.model ?? '',
  serialNumber: item.serialNumber ?? '',
  location: item.location ?? '',
  criticalityId: item.criticalityId != null ? Number(item.criticalityId) : undefined,
  statusId: item.statusId != null ? Number(item.statusId) : undefined,
  recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
  verificationStatusId: item.verificationStatusId != null ? Number(item.verificationStatusId) : undefined,
  isRotatingEquipment: item.isRotatingEquipment ?? false,
  isSafetyCritical: item.isSafetyCritical ?? false,
  isClassRelated: item.isClassRelated ?? false,
  isCylinderSpecific: item.isCylinderSpecific ?? false,
  cylinderNumber: item.cylinderNumber != null ? Number(item.cylinderNumber) : undefined,
  isActive: item.isActive ?? true,
  notes: item.notes ?? ''});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
